import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { BookOpen, Bot, MessageSquare, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import useDebouncedCallback from "@/app/useDebouncedCallback";
import { Command, CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, } from "@/components/ui/command";
import { setSelectedChat } from "@/features/chatsSlice";
import { searchSelector, setPaletteOpen } from "@/features/searchSlice";
import { sessionSelector } from "@/features/sessionSlice";
import { addErrorToast } from "@/features/toastSlice";
import { setDialogOpen, setSelectedUser } from "@/features/usersSlice";
import { GlobalSearchDocument, SetUserStatusDocument, UserStatusDocument } from "@/gql/graphql";
import { knowledgeBaseEditPath, usersPath } from "@/routes";
import { chatbotsEditPath } from "@/routes/path_helpers/chatbots";
import { chatsPath } from "@/routes/path_helpers/chats";
import { getNavItems } from "../Sidebar";
import { getCpActionCommands, getCpSettingsPages, USER_STATUS_OPTIONS } from "./values";
export default function CommandPalette() {
    const session = useAppSelector(sessionSelector);
    const { paletteOpen } = useAppSelector(searchSelector);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const doSetPaletteOpen = (open) => {
        if (!open) {
            setSearchTerm("");
        }
        dispatch(setPaletteOpen(open));
    };
    const [runSearch, { data, loading }] = useLazyQuery(GlobalSearchDocument);
    const debouncedSearch = useDebouncedCallback((value) => {
        setSearchTerm(value);
        if (value.trim().length)
            runSearch({ variables: { customerId: session.customer.id, term: value } });
    }, 300);
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                doSetPaletteOpen(true);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [setUserStatus] = useMutation(SetUserStatusDocument, {
        refetchQueries: [{ query: UserStatusDocument }],
    });
    async function setStatus(status) {
        try {
            if (!status)
                return;
            const { data } = await setUserStatus({ variables: { status } });
            if (!data?.setUserStatus) {
                throw new Error("setUserStatus is null/undefined?");
            }
            doSetPaletteOpen(false);
        }
        catch (e) {
            console.log(e);
            dispatch(addErrorToast());
            doSetPaletteOpen(false);
        }
    }
    const go = (url) => {
        doSetPaletteOpen(false);
        navigate(url);
    };
    const openChat = (chat) => {
        doSetPaletteOpen(false);
        dispatch(setSelectedChat(chat));
        navigate(chatsPath());
    };
    const openUser = (user) => {
        doSetPaletteOpen(false);
        dispatch(setSelectedUser(user));
        dispatch(setDialogOpen(true));
        navigate(usersPath());
    };
    const createUser = () => {
        doSetPaletteOpen(false);
        dispatch(setSelectedUser(null));
        dispatch(setDialogOpen(true));
        navigate(usersPath());
    };
    const result = data?.globalSearch;
    const hasResults = !!(result?.chats.length ||
        result?.users.length ||
        result?.knowledgeBases.length ||
        result?.chatbots.length);
    const mainNavItems = getNavItems(session);
    const settingsNavItems = getCpSettingsPages(session);
    const actionItems = getCpActionCommands(session, createUser);
    return (_jsx(CommandDialog, { open: paletteOpen, onOpenChange: doSetPaletteOpen, children: _jsxs(Command, { className: "rounded-lg border", children: [_jsx(CommandInput, { placeholder: "Tippe um zu suchen oder Aktionen auszuf\u00FChren\u2026", onValueChange: debouncedSearch }), _jsxs(CommandList, { className: "max-h-80", children: [_jsx(CommandGroup, { heading: "Navigation", children: mainNavItems.map((item) => item.may ? (_jsxs(CommandItem, { onSelect: () => go(item.url), children: [_jsx(item.icon, { className: "mr-2 h-4 w-4" }), " ", item.title] }, item.url)) : null) }), _jsx(CommandSeparator, {}), _jsx(CommandGroup, { heading: "Einstellungen", children: settingsNavItems.map((item) => item.may ? (_jsxs(CommandItem, { onSelect: () => go(item.url), children: [_jsx(item.icon, { className: "mr-2 h-4 w-4" }), " ", item.title] }, item.url)) : null) }), _jsx(CommandSeparator, {}), _jsx(CommandGroup, { heading: "Benutzer Status", children: USER_STATUS_OPTIONS.map((status) => (_jsxs(CommandItem, { onSelect: () => setStatus(status.value), children: [_jsx("span", { className: "mr-2 h-4 w-4", children: status.icon }), " ", status.label] }, status.value))) }), _jsx(CommandSeparator, {}), _jsx(CommandGroup, { heading: "Aktionen", children: actionItems.map((item) => item.may ? (_jsxs(CommandItem, { onSelect: () => (item?.run ? item.run() : go(item.url)), children: [_jsx(item.icon, { className: "mr-2 h-4 w-4" }), " ", item.title] }, item.key)) : null) }), _jsx(CommandSeparator, {}), loading && (_jsx(CommandGroup, { heading: "Suche", children: _jsx(CommandItem, { disabled: true, children: "lade\u2026" }) })), !loading && result && searchTerm && (_jsxs(_Fragment, { children: [!!result.chats.length && (_jsxs(_Fragment, { children: [_jsx(CommandGroup, { heading: "Chats", children: result?.chats.map((c) => (_jsxs(CommandItem, { onSelect: () => openChat(c), value: `${searchTerm} chat-${c.id}`, children: [_jsx(MessageSquare, { className: "mr-2 h-4 w-4" }), "Chat #", c.id] }, c.id))) }), _jsx(CommandSeparator, {})] })), !!result.users.length && (_jsxs(_Fragment, { children: [_jsx(CommandGroup, { heading: "Benutzer", children: result.users.map((u) => (_jsxs(CommandItem, { onSelect: () => openUser(u), value: `${searchTerm} user-${u.id}`, children: [_jsx(User, { className: "mr-2 h-4 w-4" }), u.firstname, " ", u.lastname, _jsx("span", { className: "ml-auto text-xs text-muted-foreground", children: u.email })] }, u.id))) }), _jsx(CommandSeparator, {})] })), !!result.knowledgeBases.length && (_jsxs(_Fragment, { children: [_jsx(CommandGroup, { heading: "Wissensdatenbanken", children: result.knowledgeBases.map((kb) => (_jsxs(CommandItem, { onSelect: () => go(knowledgeBaseEditPath(kb)), value: `${searchTerm} kb-${kb.id}`, children: [_jsx(BookOpen, { className: "mr-2 h-4 w-4" }), " ", kb.name] }, kb.id))) }), _jsx(CommandSeparator, {})] })), !!result.chatbots.length && (_jsxs(_Fragment, { children: [_jsx(CommandGroup, { heading: "Chatbots", children: result.chatbots.map((cb) => (_jsxs(CommandItem, { onSelect: () => go(chatbotsEditPath(cb)), value: `${searchTerm} cb-${cb.id}`, children: [_jsx(Bot, { className: "mr-2 h-4 w-4" }), " ", cb.name] }, cb.id))) }), _jsx(CommandSeparator, {})] })), !hasResults && (_jsx(CommandGroup, { heading: "Suche", children: _jsx(CommandItem, { disabled: true, value: `${searchTerm} no-result`, children: "Keine Treffer \uD83E\uDEE3" }) }))] }))] })] }) }));
}
